import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';

interface SessionWidgetState {
  activeSessionCount: number;
  progressPercent: number;
  estimatedTimeRemaining?: string;
}

interface SessionWidgetContextType {
  isMinimized: boolean;
  returnPath: string | null;
  sessionState: SessionWidgetState;
  minimizeSession: (returnPath: string, state: SessionWidgetState) => void;
  maximizeSession: () => void;
  closeSession: () => void;
  updateSessionState: (state: SessionWidgetState) => void;
}

const defaultSessionState: SessionWidgetState = {
  activeSessionCount: 0,
  progressPercent: 0
};

const SessionWidgetContext = createContext<
  SessionWidgetContextType | undefined
>(undefined);

export const SessionWidgetProvider = ({ children }) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [returnPath, setReturnPath] = useState<string | null>(null);
  const [sessionState, setSessionState] =
    useState<SessionWidgetState>(defaultSessionState);

  const minimizeSession = useCallback(
    (path: string, state: SessionWidgetState) => {
      setReturnPath(path);
      setSessionState(state);
      setIsMinimized(true);
    },
    []
  );

  const maximizeSession = useCallback(() => {
    setIsMinimized(false);
  }, []);

  const closeSession = useCallback(() => {
    setIsMinimized(false);
    setReturnPath(null);
    setSessionState(defaultSessionState);
  }, []);

  const updateSessionState = useCallback((state: SessionWidgetState) => {
    setSessionState(state);
  }, []);

  const value = useMemo(
    () => ({
      isMinimized,
      returnPath,
      sessionState,
      minimizeSession,
      maximizeSession,
      closeSession,
      updateSessionState
    }),
    [
      isMinimized,
      returnPath,
      sessionState,
      minimizeSession,
      maximizeSession,
      closeSession,
      updateSessionState
    ]
  );

  return (
    <SessionWidgetContext.Provider value={value}>
      {children}
    </SessionWidgetContext.Provider>
  );
};

export const useSessionWidget = (): SessionWidgetContextType => {
  const ctx = useContext(SessionWidgetContext);
  if (!ctx)
    throw new Error(
      'useSessionWidget must be used within SessionWidgetProvider'
    );
  return ctx;
};
